<script lang="ts" setup>
import { Head, usePage } from "@inertiajs/vue3";
import HeadingComponent from "../../Components/HeadingComponent.vue";
import { MapPinIcon, PhoneIcon } from "@heroicons/vue/24/outline";
import { ListBulletIcon, ChevronLeftIcon } from "@heroicons/vue/24/outline";

import { Event, Track } from "@/types";
import { DateTime, Duration } from "luxon";

interface Props {
    event?: Event;
    track?: Track;
    // Lai ieliktu iekš live tracking, jāpadod visi competitors ar viņu telefonu numuriem ja skatās admins,
    // tad jānolasa atteicīgi no globālā variable, lai varētu uzreiz piezvanīt.
    competitors?: Array;
}

import { onMounted, onUnmounted, reactive, ref, watch } from "vue";
import EventLiveTrackingMap from "@/Pages/EventPublic/Components/EventLiveTrackingMap.vue";
import ReplayIcon from "@/Components/assets/ReplayIcon.vue";
import Toast from "@/Components/Toast.vue";
import UpDownIcon from "@/Components/assets/UpDownIcon.vue";
import FwbTooltip from "@/Components/FwbTooltip/FwbTooltip.vue";
import proj4 from "proj4";
import { round } from "@/Helpers/helperFunctions";

import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import { EllipsisVerticalIcon } from "@heroicons/vue/20/solid";
import SpinnerIcon from "@/Components/assets/SpinnerIcon.vue";

const mapComponent = ref(null);

const pageProps = usePage().props;

const props = withDefaults(defineProps<Props>(), {});

const mapFlyTo = ref([]);
const features = ref([]);
const liveTails = ref([]);

let intervalSeconds = 5;
let liveTrackingIntervalId = null;
let showTracksSelector = false;
const isSectionsListOpen = ref(false);
const clickedCompetitor = ref(null);

const myProps = reactive({
    showWaypointLabels: true,
    showRiderTails: true,
    showRiderNames: true,
    isMapInited: false,
});

watch(
    () => myProps.showWaypointLabels,
    (newValue, oldValue) => {
        mapComponent.value.toggleWaypointLabels(newValue);
    },
);

watch(
    () => myProps.showRiderTails,
    (newValue, oldValue) => {
        mapComponent.value.toggleRiderTails(newValue);
    },
);

watch(
    () => myProps.showRiderNames,
    (newValue, oldValue) => {
        mapComponent.value.toggleRiderNames(newValue);
    },
);

function onMapInit() {
    try {
        Object.hasOwn({}, "test");
    } catch (e) {
        alert(
            "Please update your internet browser to the latest version, as your current one is outdated and may not function properly. Then, try again.",
        );
        return;
    }

    myProps.isMapInited = true;
}

function getUtmFromCoordinates(coordinates) {
    // Calculate UTM zone number
    const lat = round(coordinates[1], 6);
    const lon = round(coordinates[0], 6);

    const zoneNumber = Math.floor((lon + 180) / 6) + 1;
    const isNorthern = lat >= 0;
    const hemisphere = isNorthern ? "north" : "south";

    // Build the Proj4 UTM string dynamically
    const utmProj = `+proj=utm +zone=${zoneNumber} +datum=WGS84 +units=m +no_defs +${hemisphere}`;

    // Convert using proj4
    const [easting, northing] = proj4("EPSG:4326", utmProj, [lon, lat]);

    return `${zoneNumber}${isNorthern ? "N" : "S"} ${easting.toFixed(2)} ${northing.toFixed(2)}`;
    // return {
    //     zone: zoneNumber,
    //     hemisphere: isNorthern ? "N" : "S",
    //     easting: easting.toFixed(2),
    //     northing: northing.toFixed(2),
    // };
}

function getLiveTrackingData() {
    axios
        .get(
            route("event.public.get-live-tracking-data", {
                hashid: pageProps.event.hashid,
                seo_slug: pageProps.event.seo_slug,
            }),
        )
        .then((response) => {
            // console.log("response.data", response.data);
            if (!(response.data.live_competitors && response.data.live_competitors.features)) {
                return false;
            }
            features.value = response.data.live_competitors.features;
            liveTails.value = response.data.live_tails.features;

            // Lai ieliktu iekš live tracking, jāpadod visi competitors ar viņu telefonu numuriem ja skatās admins,
            // tad jānolasa atteicīgi no globālā variable, lai varētu uzreiz piezvanīt.
            if (pageProps.competitors) {
                for (let i = 0, l = features.value.length; i < l; i++) {
                    features.value[i].competitor = pageProps.competitors.find(
                        (c) => c.id === features.value[i].properties.id,
                    );
                }
            }

            // savādāk viņš ir redzams uzreiz, bet karte vēl nav inicializējusies
            // useris var paspēt uzklikšķināt uz checkbox pirms ieļades, kā rezultāt pilna konsole erroriem.
            showTracksSelector = true;
        })
        .finally(() => {
            liveTrackingIntervalId = setTimeout(() => {
                getLiveTrackingData();
            }, intervalSeconds * 1000);
        });
}

function findCompetitorFromPropsUsingCid(c_id) {
    // Lai ieliktu iekš live tracking, jāpadod visi competitors ar viņu telefonu numuriem ja skatās admins,
    // tad jānolasa atteicīgi no globālā variable, lai varētu uzreiz piezvanīt.
    if (!pageProps.competitors) {
        return null;
    }
    return pageProps.competitors.find((c) => c.id === c_id);
}

const loadedTrackVersionsObject = {};

function tryToLoadSectionTrack(section) {
    // console.log("class_section_id", section.pivot.id);
    // console.log("section", section.show_on_map);

    if (!Object.hasOwn(loadedTrackVersionsObject, section.id)) {
        // console.log("event.public.get-live-tracking-track");
        axios
            .get(
                route("event.public.get-live-tracking-track", {
                    hashid: pageProps.event.hashid,
                    seo_slug: pageProps.event.seo_slug,
                    class_section_id: section.pivot.id,
                }),
            )
            .then((response) => {
                if (response.data.msg) {
                    alert(response.data.msg);
                }
                if (response.data.track_version) {
                    loadedTrackVersionsObject[section.id] = response.data.track_version;
                    // console.log("loadedTrackVersionsObject", loadedTrackVersionsObject);
                    mapComponent.value.drawTrackVersionOnMap({
                        class_section_id: section.id,
                    });
                }
                // console.log("loadedTrackVersionsObject", loadedTrackVersionsObject);
            })
            .finally(() => {});
    } else {
        if (section.show_on_map) {
            mapComponent.value.hideTrackVersionFromMap({
                class_section_id: section.id,
            });
        } else {
            mapComponent.value.drawTrackVersionOnMap({
                class_section_id: section.id,
            });
        }
    }
}

let pageWidth = 1920;
// do not run in SSR
if (typeof window !== "undefined") {
    pageWidth = window.innerWidth || document.body.clientWidth;
}
const treshold = Math.max(1, Math.floor(0.01 * pageWidth));
let touchstartX = 0;
let touchstartY = 0;
let touchendX = 0;
let touchendY = 0;
const limit = Math.tan(((45 * 1.5) / 180) * Math.PI);

function handleTouchMoveLeftMenu(e) {
    const x = touchendX - touchstartX;
    const y = touchendY - touchstartY;
    const xy = Math.abs(x / y);
    const yx = Math.abs(y / x);
    if (Math.abs(x) > treshold || Math.abs(y) > treshold) {
        if (yx <= limit) {
            if (x < 0) {
                // console.log("left");
                staticDesktopSidebarOpen.value = false;
            } else {
                // console.log("right");
            }
        }
        if (xy <= limit) {
            if (y < 0) {
                // console.log("top");
            } else {
                // console.log("bottom");
            }
        }
    } else {
        // console.log("tap");
    }
}

onMounted(() => {
    if (pageProps.is_live_map_available) {
        if (pageProps.canViewLiveTrackingWNoDelay) {
            intervalSeconds = 1.5;
        }

        getLiveTrackingData();
    }

    const gestureZone = document.getElementById("live-left-menu-swiper");

    gestureZone.addEventListener(
        "touchstart",
        function (event) {
            touchstartX = event.changedTouches[0].screenX;
            touchstartY = event.changedTouches[0].screenY;
        },
        false,
    );

    gestureZone.addEventListener(
        "touchend",
        function (event) {
            touchendX = event.changedTouches[0].screenX;
            touchendY = event.changedTouches[0].screenY;
            handleTouchMoveLeftMenu(event);
        },
        false,
    );
});

onUnmounted(() => {
    if (liveTrackingIntervalId) {
        clearTimeout(liveTrackingIntervalId);
    }
});

const staticDesktopSidebarOpen = ref(true);
</script>

<template>
    <Toast />
    <Head>
        <title>Live tracking: {{ $page.props.event ? $page.props.event.name : $page.props.track.name }}</title>

        <meta
            v-if="event"
            property="og:title"
            head-key="og:title"
            :content="`Live tracking: ${$page.props.event ? $page.props.event.name : $page.props.track.name}`"
        />
    </Head>

    <div
        v-if="clickedCompetitor"
        class="absolute bottom-8 right-4 z-20 min-w-48 bg-white bg-opacity-70 p-2"
    >
        <div>
            <div class="flex justify-between">
                <div class="whitespace-nowrap px-3 py-1 font-semibold">
                    #{{ clickedCompetitor.properties.competitor_number }}
                    {{ clickedCompetitor.properties.driver_full_name }}
                </div>
                <div
                    class="cursor-pointer text-lg"
                    @click="clickedCompetitor = null"
                >
                    X
                </div>
            </div>
            <a
                v-if="clickedCompetitor.properties.competitor_class_section_id"
                target="_blank"
                class="force-unvisited block px-3 py-1 text-sm leading-6"
                :href="
                    route('event.public.replay', {
                        hashid: event.hashid,
                        seo_slug: event.seo_slug,
                    }) +
                    '#ccs=|' +
                    clickedCompetitor.properties.competitor_class_section_id
                "
            >
                <ReplayIcon class="inline size-5"></ReplayIcon>&nbsp;Open Replay
            </a>
            <a
                v-if="clickedCompetitor.properties.ts_ms"
                target="_blank"
                class="force-unvisited block px-3 py-1 text-sm leading-6"
                :href="
                    'https://www.google.com/maps?q=' +
                    [...clickedCompetitor.geometry.coordinates].reverse().join(',')
                "
            >
                <svg
                    class="inline size-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    ></path></svg
                >&nbsp;Navigate to last location
            </a>
            <div class="text-sm">
                {{
                    [...clickedCompetitor.geometry.coordinates]
                        .reverse()
                        .map((el) => round(el, 6))
                        .join(",")
                }}
            </div>
            <div class="text-sm">UTM: {{ getUtmFromCoordinates(clickedCompetitor.geometry.coordinates) }}</div>
            <FwbTooltip
                v-if="findCompetitorFromPropsUsingCid(clickedCompetitor.properties.id)"
                placement="left"
            >
                <template #trigger>
                    <a
                        :href="`https://wa.me/${findCompetitorFromPropsUsingCid(clickedCompetitor.properties.id).reg_form.driver.phone}?text=Hi%2C%20EVENT organizer here!`"
                        target="_blank"
                        class="force-unvisited block px-3 py-1 text-sm leading-6 text-gray-900"
                        ><img
                            class="inline size-5"
                            src="/img/whatsapp-logo.png"
                            alt="WhatsApp"
                        />&nbsp;Chat on WhatsApp</a
                    >
                </template>
                <template #content>
                    <div>
                        {{
                            findCompetitorFromPropsUsingCid(clickedCompetitor.properties.id).reg_form.driver
                                .phone
                        }}
                    </div>
                </template>
            </FwbTooltip>
            <FwbTooltip
                v-if="findCompetitorFromPropsUsingCid(clickedCompetitor.properties.id)"
                placement="left"
            >
                <template #trigger>
                    <a
                        :href="`tel:${findCompetitorFromPropsUsingCid(clickedCompetitor.properties.id).reg_form.driver.phone}`"
                        target="_blank"
                        class="force-unvisited block px-3 py-1 text-sm leading-6"
                    >
                        <PhoneIcon class="inline size-5"></PhoneIcon>&nbsp;Call
                        {{
                            findCompetitorFromPropsUsingCid(clickedCompetitor.properties.id).reg_form.driver
                                .phone
                        }}</a
                    >
                </template>
                <template #content>
                    <div>
                        Driver phone:
                        {{
                            findCompetitorFromPropsUsingCid(clickedCompetitor.properties.id).reg_form.driver
                                .phone
                        }}
                    </div>
                    <div
                        v-if="
                            findCompetitorFromPropsUsingCid(clickedCompetitor.properties.id).reg_form.co_driver
                                .phone
                        "
                    >
                        Co-Driver phone:
                        {{
                            findCompetitorFromPropsUsingCid(clickedCompetitor.properties.id).reg_form.co_driver
                                .phone
                        }}
                    </div>
                    <div
                        v-if="
                            findCompetitorFromPropsUsingCid(clickedCompetitor.properties.id).reg_form.driver
                                .emergency_phone
                        "
                    >
                        Emergency family member:
                        {{
                            findCompetitorFromPropsUsingCid(clickedCompetitor.properties.id).reg_form.driver
                                .emergency_phone
                        }}
                    </div>
                </template>
            </FwbTooltip>
        </div>
    </div>

    <!-- Static sidebar for desktop -->
    <transition
        v-show="staticDesktopSidebarOpen"
        enter-active-class="transition ease-in-out duration-300 transform"
        enter-from-class="-translate-x-full"
        enter-to-class="translate-x-0"
        leave-active-class="transition ease-in-out duration-300 transform"
        leave-from-class="translate-x-0"
        leave-to-class="-translate-x-full"
        appear
    >
        <div class="fixed inset-y-0 z-30 flex h-screen w-80 flex-col bg-opacity-40 sm:w-96">
            <div
                class="scrollbar-hide flex grow flex-col gap-y-5 overflow-y-scroll bg-slate-900 bg-opacity-30 pb-4"
            >
                <nav
                    id="live-left-menu-swiper"
                    class="flex flex-1 flex-col"
                >
                    <div class="relative z-10 mb-2">
                        <HeadingComponent class="w-80 bg-white bg-opacity-80 p-4 shadow-xl sm:w-96">
                            <template #name>Live tracking</template>
                            <template #description>
                                <div class="mb-2">
                                    <a
                                        v-if="event"
                                        target="_top"
                                        :href="
                                            route('event.public.information', {
                                                hashid: event.hashid,
                                                seo_slug: event.seo_slug,
                                            })
                                        "
                                        >{{ event.name }}</a
                                    >
                                    <a
                                        v-if="track"
                                        target="_top"
                                        :href="
                                            route('track.public.information', {
                                                pin: track.pin,
                                                seo_slug: track.seo_slug,
                                            })
                                        "
                                        >{{ track.name }}</a
                                    >
                                </div>
                                <div
                                    v-if="!event.is_live_map_available"
                                    class="mb-4 flex rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800"
                                    role="alert"
                                >
                                    <svg
                                        class="me-3 mt-[2px] inline size-4 shrink-0"
                                        aria-hidden="true"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                    >
                                        <path
                                            d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"
                                        />
                                    </svg>
                                    <div>
                                        <span class="font-medium">Event not in progress</span>
                                        <div class="mt-2 text-sm">
                                            A map with live tracking is available only while the event is in
                                            progress.
                                        </div>
                                        <div
                                            v-if="!event.is_ended"
                                            class="mt-6 font-semibold"
                                        >
                                            Live tracking opens at:
                                            {{
                                                DateTime.fromISO(event.start_at)
                                                    .minus({ minutes: 60 })
                                                    .setZone(event.timezone)
                                                    .toLocaleString(DateTime.DATETIME_SHORT)
                                            }}
                                        </div>
                                    </div>
                                </div>
                                <template v-else>
                                    <div
                                        v-if="$page.props.canViewLiveTrackingWNoDelay"
                                        class="text-orange-500"
                                    >
                                        Live tracking without delay (you are logged in as the event organizer
                                        team member)
                                    </div>
                                    <div
                                        v-if="!$page.props.canViewLiveTrackingWNoDelay"
                                        class="text-orange-500"
                                    >
                                        <ul>
                                            <li>
                                                Live tracking has a
                                                {{ $page.props.event.live_tracking_delay_m }}-minute delay.
                                            </li>
                                            <li>
                                                Real-time live tracking is available only to event
                                                administrators.
                                            </li>
                                        </ul>
                                    </div>
                                </template>
                            </template>
                        </HeadingComponent>
                    </div>
                    <div
                        v-if="event.is_live_map_available"
                        class="w-80 bg-white bg-opacity-80 p-4 shadow-xl sm:w-96"
                    >
                        <div class="flex cursor-pointer flex-row items-center bg-white p-2 font-semibold">
                            <div>Sections</div>

                            <div
                                class="flex flex-1 cursor-pointer flex-row justify-between"
                                @click="isSectionsListOpen = !isSectionsListOpen"
                            >
                                <span class="ml-6 flex h-7 items-center">
                                    <UpDownIcon :is-open="isSectionsListOpen"></UpDownIcon>
                                </span>
                            </div>
                        </div>
                        <transition
                            :appear="isSectionsListOpen"
                            enter-active-class="duration-300 transition-all"
                            enter-from-class="max-h-0"
                            enter-to-class="max-h-96"
                            leave-active-class="duration-300 transition-all"
                            leave-from-class="max-h-96"
                            leave-to-class="max-h-0"
                        >
                            <div
                                v-show="isSectionsListOpen"
                                class="max-h-0 overflow-hidden bg-white"
                            >
                                <SpinnerIcon
                                    v-show="myProps.isMapInited === false"
                                    class="m-6 size-16 self-center"
                                ></SpinnerIcon>
                                <table
                                    v-if="showTracksSelector && myProps.isMapInited"
                                    class="mb-2 w-full table-auto border"
                                >
                                    <tbody>
                                        <template v-for="eventClass in event.classes">
                                            <tr
                                                v-for="section in eventClass.sections"
                                                :key="section.pivot.id"
                                                class="p-2 text-sm"
                                            >
                                                <td class="p-2 py-1.5 text-sm leading-5">
                                                    <FwbTooltip
                                                        placement="right"
                                                        class="inline-flex"
                                                    >
                                                        <template #trigger>
                                                            <label
                                                                :for="`track-show-${section.pivot.id}`"
                                                                class="cursor-pointer"
                                                            >
                                                                <input
                                                                    :id="`track-show-${section.pivot.id}`"
                                                                    v-model="section.show_on_map"
                                                                    type="checkbox"
                                                                    @click="tryToLoadSectionTrack(section)"
                                                                />
                                                                {{ section.name }}
                                                                <div class="text-xs text-gray-400">
                                                                    First start at:
                                                                    {{
                                                                        DateTime.fromISO(
                                                                            section.pivot.first_start_at,
                                                                        )
                                                                            .setZone(event.timezone)
                                                                            .toLocaleString(
                                                                                DateTime.DATETIME_SHORT,
                                                                            )
                                                                    }}
                                                                    ({{ eventClass.name }})
                                                                </div>
                                                            </label>
                                                        </template>
                                                        <template #content>
                                                            Section type:
                                                            {{
                                                                $page.props.app.section_types[section.type].name
                                                            }}
                                                            <br />
                                                            Start type:
                                                            {{
                                                                $page.props.app.section_start_types[
                                                                    section.pivot.start_type
                                                                ].name
                                                            }}
                                                            <br />
                                                            Global speed limit: {{ section.pivot.speed_limit }}
                                                            <br />
                                                        </template>
                                                    </FwbTooltip>
                                                </td>
                                            </tr>
                                        </template>
                                    </tbody>
                                </table>
                                <div
                                    v-if="myProps.isMapInited"
                                    class="mb-2 p-2"
                                >
                                    <label>
                                        <input
                                            v-model="myProps.showWaypointLabels"
                                            type="checkbox"
                                        />
                                        Show waypoint labels &nbsp;
                                    </label>
                                    <br />
                                    <label>
                                        <input
                                            v-model="myProps.showRiderNames"
                                            type="checkbox"
                                        />
                                        Show rider names &nbsp;
                                    </label>
                                    <br />
                                    <label>
                                        <input
                                            v-model="myProps.showRiderTails"
                                            type="checkbox"
                                        />
                                        Show rider tails (1min) &nbsp;
                                    </label>
                                </div>
                            </div>
                        </transition>

                        <div class="mt-4 text-xs">
                            <span class="font-medium text-green-500">Green</span>: OK, riding <br />
                            <span class="font-medium text-orange-500">Orange</span>: riding under 5 km/h <br />
                            <span class="font-medium text-red-500">Red</span>: stopped <br />
                            <span class="font-medium text-gray-500">Gray</span>: location data more than 1
                            minute old <br />
                            <span class="font-medium text-black">Black</span>: location data more than 5 minutes
                            old <br />
                        </div>

                        <br />

                        <div class="bg-white p-2">
                            <div class="font-semibold">Competitors ({{ features.length }})</div>
                            <div
                                v-if="features.length === 0"
                                class="py-2 text-sm"
                            >
                                There are currently no competitors online
                            </div>
                            <ul
                                role="list"
                                class="divide-y divide-gray-100 bg-white p-2"
                            >
                                <li
                                    v-for="item in features"
                                    :key="item.properties.competitor_class_section_id"
                                    class="flex items-center justify-between gap-x-6 py-3"
                                >
                                    <div
                                        class="min-w-0 cursor-pointer"
                                        @click="mapFlyTo = item.geometry.coordinates"
                                    >
                                        <div class="flex items-center gap-x-3">
                                            <div
                                                :class="[
                                                    `bg-${item.properties.color}-400/10 text-${item.properties.color}-400`,
                                                    'flex-none rounded-full p-1',
                                                ]"
                                            >
                                                <div class="size-2 rounded-full bg-current" />
                                            </div>
                                            <p class="text-sm font-semibold leading-6 text-gray-900">
                                                <FwbTooltip>
                                                    <template #trigger>
                                                        <span class="text-sm font-bold text-orange-400"
                                                            >#{{
                                                                item.properties.competitor_number ?? "-"
                                                            }}</span
                                                        >
                                                        <span
                                                            :class="
                                                                item.properties.status === 'tester'
                                                                    ? 'text-gray-300'
                                                                    : ''
                                                            "
                                                            >&nbsp;{{ item.properties.driver_full_name }}</span
                                                        >
                                                        <template v-if="item.properties.co_driver_full_name">
                                                            <br />{{ item.properties.co_driver_full_name }}
                                                        </template>
                                                    </template>
                                                    <template #content>
                                                        <div>
                                                            <div v-if="item.properties.status === 'tester'">
                                                                Status: Tester <br />
                                                                <br />
                                                            </div>
                                                            {{ item.properties.c_class }} <br />
                                                            {{ item.properties.vehicle_name }}
                                                        </div>
                                                    </template>
                                                </FwbTooltip>
                                            </p>
                                            <!--                                            <p
                                                :class="[
                                                    statuses[project.status],
                                                    'mt-0.5 whitespace-nowrap rounded-md px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset',
                                                ]"
                                            >
                                                {{ project.status }}
                                            </p>-->
                                        </div>
                                        <div
                                            class="mt-1 flex items-center gap-x-2 text-xs leading-5 text-gray-500"
                                        >
                                            <p
                                                :class="[
                                                    `text-${item.properties.color}-600`,
                                                    'whitespace-nowrap',
                                                ]"
                                            >
                                                {{ item.properties.speed }} km/h <br />
                                            </p>
                                            <svg
                                                viewBox="0 0 2 2"
                                                class="size-0.5 fill-current"
                                            >
                                                <circle
                                                    cx="1"
                                                    cy="1"
                                                    r="1"
                                                />
                                            </svg>
                                            <p class="truncate">
                                                Last loc:
                                                <span
                                                    v-if="item.properties.ts_ms"
                                                    :title="item.properties.ts_ms"
                                                >
                                                    {{
                                                        Duration.fromMillis(
                                                            item.properties.diff_s * 1000,
                                                        ).toFormat(
                                                            item.properties.diff_s < 60 ? "s's'" : "m'm' s's'",
                                                        )
                                                    }}
                                                </span>
                                                <span v-else>-</span>
                                            </p>
                                        </div>
                                    </div>
                                    <div class="flex flex-none items-center gap-x-4">
                                        <Menu
                                            as="div"
                                            class="relative flex-none"
                                        >
                                            <MenuButton
                                                class="-m-2.5 block p-2.5 text-gray-500 hover:text-gray-900"
                                            >
                                                <span class="sr-only">Open options</span>
                                                <EllipsisVerticalIcon
                                                    class="size-5"
                                                    aria-hidden="true"
                                                />
                                            </MenuButton>
                                            <transition
                                                enter-active-class="transition ease-out duration-100"
                                                enter-from-class="transform opacity-0 scale-95"
                                                enter-to-class="transform opacity-100 scale-100"
                                                leave-active-class="transition ease-in duration-75"
                                                leave-from-class="transform opacity-100 scale-100"
                                                leave-to-class="transform opacity-0 scale-95"
                                            >
                                                <MenuItems
                                                    class="absolute right-0 z-10 mt-2 w-44 origin-top-right space-y-2 rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none"
                                                >
                                                    <MenuItem v-slot="{ active }">
                                                        <a
                                                            href="#"
                                                            :class="[
                                                                active ? 'bg-gray-50' : '',
                                                                'force-unvisited block px-3 py-1 text-sm leading-6 text-gray-900',
                                                            ]"
                                                            @click="mapFlyTo = item.geometry.coordinates"
                                                        >
                                                            <MapPinIcon class="inline size-5"></MapPinIcon
                                                            >&nbsp;Show on map</a
                                                        >
                                                    </MenuItem>
                                                    <MenuItem v-slot="{ active }">
                                                        <a
                                                            v-if="item.properties.competitor_class_section_id"
                                                            target="_blank"
                                                            :class="[
                                                                active ? 'bg-gray-50' : '',
                                                                'force-unvisited block px-3 py-1 text-sm leading-6',
                                                            ]"
                                                            :href="
                                                                route('event.public.replay', {
                                                                    hashid: event.hashid,
                                                                    seo_slug: event.seo_slug,
                                                                }) +
                                                                '#ccs=|' +
                                                                item.properties.competitor_class_section_id
                                                            "
                                                        >
                                                            <ReplayIcon class="inline size-5"></ReplayIcon
                                                            >&nbsp;Open Replay
                                                        </a>
                                                    </MenuItem>
                                                    <MenuItem v-slot="{ active }">
                                                        <a
                                                            v-if="item.properties.ts_ms"
                                                            target="_blank"
                                                            :class="[
                                                                active ? 'bg-gray-50' : '',
                                                                'force-unvisited block px-3 py-1 text-sm leading-6',
                                                            ]"
                                                            :href="
                                                                'https://www.google.com/maps?q=' +
                                                                [...item.geometry.coordinates]
                                                                    .reverse()
                                                                    .join(',')
                                                            "
                                                        >
                                                            <svg
                                                                class="inline size-5"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <path
                                                                    stroke-linecap="round"
                                                                    stroke-linejoin="round"
                                                                    stroke-width="2"
                                                                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                                                ></path></svg
                                                            >&nbsp;Navigate to last location
                                                        </a>
                                                    </MenuItem>
                                                    <MenuItem
                                                        v-if="item.competitor"
                                                        v-slot="{ active }"
                                                    >
                                                        <FwbTooltip
                                                            placement="right"
                                                            class="inline-flex"
                                                        >
                                                            <template #trigger>
                                                                <a
                                                                    :href="`https://wa.me/${item.competitor.reg_form.driver.phone}?text=Hi%2C%20EVENT organizer here!`"
                                                                    target="_blank"
                                                                    :class="[
                                                                        active ? 'bg-gray-50' : '',
                                                                        'force-unvisited block px-3 py-1 text-sm leading-6 text-gray-900',
                                                                    ]"
                                                                    ><img
                                                                        class="inline size-5"
                                                                        src="/img/whatsapp-logo.png"
                                                                        alt="WhatsApp"
                                                                    />&nbsp;Chat on WhatsApp</a
                                                                >
                                                            </template>
                                                            <template #content>
                                                                <div>
                                                                    {{ item.competitor.reg_form.driver.phone }}
                                                                </div>
                                                            </template>
                                                        </FwbTooltip>
                                                    </MenuItem>
                                                    <MenuItem
                                                        v-if="item.competitor"
                                                        v-slot="{ active }"
                                                    >
                                                        <FwbTooltip placement="right">
                                                            <template #trigger>
                                                                <a
                                                                    :href="`tel:${item.competitor.reg_form.driver.phone}`"
                                                                    target="_blank"
                                                                    :class="[
                                                                        active ? 'bg-gray-50' : '',
                                                                        'force-unvisited block px-3 py-1 text-sm leading-6 text-gray-900',
                                                                    ]"
                                                                >
                                                                    <PhoneIcon class="inline size-5"></PhoneIcon
                                                                    >&nbsp;Call
                                                                    {{
                                                                        item.competitor.reg_form.driver.phone
                                                                    }}</a
                                                                >
                                                            </template>
                                                            <template #content>
                                                                <div>
                                                                    Driver phone:
                                                                    {{ item.competitor.reg_form.driver.phone }}
                                                                </div>
                                                                <div
                                                                    v-if="
                                                                        item.competitor.reg_form.co_driver.phone
                                                                    "
                                                                >
                                                                    Co-Driver phone:
                                                                    {{
                                                                        item.competitor.reg_form.co_driver.phone
                                                                    }}
                                                                </div>
                                                                <div
                                                                    v-if="
                                                                        item.competitor.reg_form.driver
                                                                            .emergency_phone
                                                                    "
                                                                >
                                                                    Emergency family member:
                                                                    {{
                                                                        item.competitor.reg_form.driver
                                                                            .emergency_phone
                                                                    }}
                                                                </div>
                                                            </template>
                                                        </FwbTooltip>
                                                    </MenuItem>
                                                </MenuItems>
                                            </transition>
                                        </Menu>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </nav>
            </div>
        </div>
    </transition>

    <div>
        <div
            :class="staticDesktopSidebarOpen ? 'hidden' : ''"
            class="absolute left-3 top-3 z-20 flex shrink-0 cursor-pointer items-center rounded-full bg-white bg-opacity-80 px-6 py-4 shadow-sm"
            @click="staticDesktopSidebarOpen = !staticDesktopSidebarOpen"
        >
            <span class="sr-only">Open sidebar</span>
            <ListBulletIcon
                class="size-6"
                aria-hidden="true"
            />
        </div>

        <div
            :class="!staticDesktopSidebarOpen ? 'hidden' : ''"
            class="absolute left-60 top-3 z-40 ml-2 flex shrink-0 cursor-pointer sm:left-96 sm:ml-3"
        >
            <button
                type="button"
                class="rounded-xl bg-gray-400 p-2.5"
                @click="staticDesktopSidebarOpen = false"
            >
                <span class="sr-only">Close sidebar</span>
                <ChevronLeftIcon
                    class="size-6 text-white"
                    aria-hidden="true"
                />
            </button>
        </div>

        <main>
            <EventLiveTrackingMap
                ref="mapComponent"
                :map-fly-to="mapFlyTo"
                :loaded-track-versions-object="loadedTrackVersionsObject"
                :features="features"
                :live_tails="liveTails"
                :is_live_map_available="$page.props.is_live_map_available"
                class="h-screen flex-1"
                @on-map-init="
                    (val) => {
                        onMapInit();
                    }
                "
                @clicked-on-competitor="
                    (val) => {
                        clickedCompetitor = val;
                    }
                "
                @tracepoint-clicked="
                    (feature) => {
                        modalDeleteTracepoints.show = true;
                        modalDeleteTracepoints.feature = feature;
                    }
                "
            />
        </main>
    </div>
</template>
